// The imprint and the support page, in English and German (du). The company
// details are Panter's own (`facts.ts`); the security contact is the one
// `/.well-known/security.txt` already publishes (`apps/web/nginx.conf.template`).

import { PUBLIC_PRODUCT_NAME } from "@loom/shared/brand";

import { PANTER } from "./facts";
import { list, p, rows, type LegalDoc, type LegalLang } from "./types";

const reps = PANTER.representatives.join(", ");

const imprintEn: LegalDoc = {
  title: "Imprint",
  intro: `${PUBLIC_PRODUCT_NAME} is made and run by Panter AG.`,
  sections: [
    {
      id: "company",
      heading: "Company",
      blocks: [
        rows(
          ["Company", PANTER.legalName],
          ["Address", `${PANTER.street}, ${PANTER.city}, Switzerland`],
          ["E-mail", PANTER.imprintEmail],
          ["Phone", PANTER.phone],
          ["Website", PANTER.website],
        ),
      ],
    },
    {
      id: "register",
      heading: "Register",
      blocks: [
        rows(
          ["Authorised to represent", reps],
          ["Commercial register", PANTER.register],
          ["Company number (UID)", PANTER.uid],
          ["VAT number", PANTER.vat],
        ),
      ],
    },
    {
      id: "beta",
      heading: `${PUBLIC_PRODUCT_NAME} is in beta`,
      blocks: [
        p(
          `${PUBLIC_PRODUCT_NAME} is still being built. Check what it writes into your projects before you rely on it.`,
        ),
      ],
    },
  ],
};

const imprintDe: LegalDoc = {
  title: "Impressum",
  intro: `${PUBLIC_PRODUCT_NAME} wird von der Panter AG gebaut und betrieben.`,
  sections: [
    {
      id: "company",
      heading: "Firma",
      blocks: [
        rows(
          ["Firma", PANTER.legalName],
          ["Adresse", `${PANTER.street}, ${PANTER.city}, Schweiz`],
          ["E-Mail", PANTER.imprintEmail],
          ["Telefon", PANTER.phone],
          ["Website", PANTER.website],
        ),
      ],
    },
    {
      id: "register",
      heading: "Handelsregister",
      blocks: [
        rows(
          ["Vertretungsberechtigte Personen", reps],
          ["Handelsregistereintrag", PANTER.register],
          ["Unternehmens-Identifikationsnummer (UID)", PANTER.uid],
          ["Mehrwertsteuernummer", PANTER.vat],
        ),
      ],
    },
    {
      id: "beta",
      heading: `${PUBLIC_PRODUCT_NAME} ist in der Beta`,
      blocks: [
        p(
          `${PUBLIC_PRODUCT_NAME} ist noch im Aufbau. Prüf, was es in deine Projekte schreibt, bevor du dich darauf verlässt.`,
        ),
      ],
    },
  ],
};

const supportEn: LegalDoc = {
  title: "Support",
  intro: `A question about ${PUBLIC_PRODUCT_NAME}, something that does not work, or a wish: write to us.`,
  sections: [
    {
      id: "contact",
      heading: "Contact",
      blocks: [
        rows(["E-mail", PANTER.contactEmail], ["Phone", PANTER.phone]),
        p(
          "If something went wrong in a chat, tell us the project and roughly when it happened. Please do not send logins or passwords.",
        ),
      ],
    },
    {
      id: "data",
      heading: "Your data",
      blocks: [
        list(
          `To see, get a copy of or delete what ${PUBLIC_PRODUCT_NAME} keeps about you, write to ${PANTER.imprintEmail}. The privacy page says what that is.`,
          `To delete one chat yourself, reset it in ${PUBLIC_PRODUCT_NAME}.`,
          `To remove ${PUBLIC_PRODUCT_NAME}'s access, sign out, or disconnect Figma or GitHub on your account page.`,
        ),
      ],
    },
    {
      id: "security",
      heading: "Security problems",
      blocks: [
        p(
          `Found a security problem in ${PUBLIC_PRODUCT_NAME}? The contact is in https://ai-workbench.panter.ch/.well-known/security.txt.`,
        ),
      ],
    },
  ],
};

const supportDe: LegalDoc = {
  title: "Support",
  intro: `Eine Frage zu ${PUBLIC_PRODUCT_NAME}, etwas funktioniert nicht, oder ein Wunsch: Schreib uns.`,
  sections: [
    {
      id: "contact",
      heading: "Kontakt",
      blocks: [
        rows(["E-Mail", PANTER.contactEmail], ["Telefon", PANTER.phone]),
        p(
          "Ist in einem Chat etwas schiefgelaufen, nenn uns das Projekt und ungefähr den Zeitpunkt. Schick uns bitte keine Logins oder Passwörter.",
        ),
      ],
    },
    {
      id: "data",
      heading: "Deine Daten",
      blocks: [
        list(
          `Um zu sehen, was ${PUBLIC_PRODUCT_NAME} über dich behält, eine Kopie zu bekommen oder es löschen zu lassen, schreib an ${PANTER.imprintEmail}. Was das ist, steht auf der Datenschutzseite.`,
          `Einen einzelnen Chat löschst du selbst, indem du ihn in ${PUBLIC_PRODUCT_NAME} zurücksetzt.`,
          `Um ${PUBLIC_PRODUCT_NAME} den Zugang zu nehmen, melde dich ab, oder trenne Figma oder GitHub auf deiner Kontoseite.`,
        ),
      ],
    },
    {
      id: "security",
      heading: "Sicherheitsprobleme",
      blocks: [
        p(
          `Du hast ein Sicherheitsproblem in ${PUBLIC_PRODUCT_NAME} gefunden? Der Kontakt steht in https://ai-workbench.panter.ch/.well-known/security.txt.`,
        ),
      ],
    },
  ],
};

export const IMPRINT: Readonly<Record<LegalLang, LegalDoc>> = { en: imprintEn, de: imprintDe };
export const SUPPORT: Readonly<Record<LegalLang, LegalDoc>> = { en: supportEn, de: supportDe };
